import React, { useState, useRef, useEffect } from "react";
import { OSShell } from "../components/OSShell";
import { Map, Mountain, Backpack, ShoppingBag, Cloud, Newspaper, FileText, Folder, Tent, Building2, Spade, BookOpen } from "lucide-react";
import TripPlannerPage from "./TripPlannerPage";
import PlanPage from "./PlanPage";
import GearPage from "./GearPage";
import VendorsPage from "./VendorsPage";
import ProvidersPage from "./ProvidersPage";
import HowToPage from "./HowToPage";
import WeatherApp from "../components/WeatherApp";
import HeadlinesPage from "./HeadlinesPage";
import Notepad from "../components/Notepad";
import CampgroundInfo from "../components/CampgroundInfo";
import FacilityFinder from "../components/FacilityFinder";
import Solitaire from "../components/Solitaire";
import FacilitiesSyncAdmin from "../components/FacilitiesSyncAdmin";

// System Info Component
const SystemInfo = () => (
  <div style={{ padding: '16px', fontFamily: 'Arial, sans-serif' }}>
    <h2 style={{ marginTop: 0, marginBottom: '16px', fontSize: '14px', fontWeight: 'bold' }}>
      Outdoorec OS Shell
    </h2>
    <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
      <p style={{ marginBottom: '12px' }}>
        <strong>Version:</strong> 1.0.0
      </p>
      <p style={{ marginBottom: '12px' }}>
        <strong>Description:</strong> A nostalgic recreation inspired by classic operating systems including Windows 95, macOS, Windows 11, and Linux GNOME.
      </p>
      <div style={{ 
        marginTop: '16px', 
        paddingTop: '12px', 
        borderTop: '1px solid #ccc',
        fontSize: '11px',
        color: '#666'
      }}>
        <p style={{ marginBottom: '8px' }}>
          <strong>Disclaimer:</strong> This is a fan recreation for educational and entertainment purposes. It is not affiliated with, endorsed by, or associated with Microsoft, Apple, Canonical, or any other technology company.
        </p>
        <p style={{ marginBottom: '8px' }}>
          Windows is a trademark of Microsoft Corporation. macOS is a trademark of Apple Inc. GNOME is a trademark of GNOME Foundation. These trademarks are used for informational purposes only to describe the visual styles being recreated.
        </p>
        <p>
          © 2026 Outdoorec. All rights reserved.
        </p>
      </div>
    </div>
  </div>
);

// Styled Lucide Icons with retro colors and effects
const StyledIcon = ({ Icon, color, bgColor, label }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    borderRadius: '4px',
    background: bgColor,
    border: '2px solid #000',
    boxShadow: 'inset 1px 1px 0 rgba(255,255,255,0.5), inset -1px -1px 0 rgba(0,0,0,0.5), 2px 2px 4px rgba(0,0,0,0.3)',
  }}>
    <Icon size={24} color={color} strokeWidth={2} />
  </div>
);

// Folder Icon Component
const FolderIcon = ({ color = "#dfb939", size = 40 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M3 3h8l2 2h8a2 2 0 012 2v12a2 2 0 01-2 2H3a2 2 0 01-2-2V5a2 2 0 012-2z" fill={color} />
    <path d="M3 3h8l2 2h8a2 2 0 012 2v12a2 2 0 01-2 2H3a2 2 0 01-2-2V5a2 2 0 012-2z" stroke="#333" strokeWidth="0.5" />
  </svg>
);

// Icon definitions - removed, now using styled lucide icons below

// Folder Icon Component
const ContextMenu = ({ x, y, items, theme, onClose }) => {
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      style={{
        position: "fixed",
        left: `${x}px`,
        top: `${y}px`,
        background: theme.window,
        border: `2px solid`,
        borderColor: `${theme.border.top} ${theme.border.right} ${theme.border.bottom} ${theme.border.left}`,
        zIndex: 10000,
        minWidth: "150px",
      }}
    >
      {items.map((item, idx) => (
        <div
          key={idx}
          onClick={() => {
            item.onClick();
            onClose();
          }}
          style={{
            padding: "4px 8px",
            cursor: item.disabled ? "default" : "pointer",
            backgroundColor: item.disabled ? "transparent" : "transparent",
            color: item.disabled ? "#999999" : theme.text,
            fontSize: "12px",
            borderBottom: idx < items.length - 1 ? `1px solid ${theme.border.bottom}` : "none",
          }}
          onMouseEnter={(e) => {
            if (!item.disabled) e.target.style.backgroundColor = "#000080";
            if (!item.disabled) e.target.style.color = "#ffffff";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = "transparent";
            e.target.style.color = theme.text;
          }}
        >
          {item.label}
        </div>
      ))}
    </div>
  );
};

const OSShellDemoPage = () => {
  const shellRef = useRef(null);
  const [windows, setWindows] = useState([]);
  const [focusedWindowId, setFocusedWindowId] = useState(null);
  const [zIndex, setZIndex] = useState(1000);
  const [osStyle, setOsStyle] = useState('windows95');
  const [weatherLocation, setWeatherLocation] = useState({
    latitude: 40.7128,
    longitude: -74.0060,
    locationName: "New York",
  });
  const [fileExplorerState, setFileExplorerState] = useState({
    selectedIconId: null,
    currentPath: [],
  });

  // Theme definitions
  const themes = {
    windows95: {
      name: 'Windows 95',
      desktop: '#008080',
      window: '#c0c0c0',
      border: { top: '#dfdfdf', left: '#dfdfdf', right: '#808080', bottom: '#808080' },
      title: 'linear-gradient(90deg, #000080 0%, #1084d7 100%)',
      text: '#ffffff',
      taskbar: '#c0c0c0',
      taskbarBorder: '#dfdfdf',
      taskbarText: '#000000',
      iconBg: '#008080',
      iconText: '#ffffff',
      selectedIconBg: '#000080',
      selectionColor: '#000080',
      selectionBg: 'rgba(0, 0, 128, 0.2)',
    },
    macos: {
      name: 'macOS Classic',
      desktop: '#f0f0f0',
      window: '#f5f5f5',
      border: { top: '#f9f9f9', left: '#f9f9f9', right: '#a3a3a3', bottom: '#a3a3a3' },
      title: 'linear-gradient(180deg, #dfdfdf 0%, #c0c0c0 100%)',
      text: '#000000',
      taskbar: '#efefef',
      taskbarBorder: '#dfdfdf',
      taskbarText: '#000000',
      iconBg: '#f0f0f0',
      iconText: '#000000',
      selectedIconBg: '#b0b0b0',
      selectionColor: '#4a90e2',
      selectionBg: 'rgba(74, 144, 226, 0.2)',
    },
    windows11: {
      name: 'Windows 11',
      desktop: '#ffffff',
      window: '#f3f3f3',
      border: { top: '#f3f3f3', left: '#f3f3f3', right: '#e0e0e0', bottom: '#e0e0e0' },
      title: 'linear-gradient(90deg, #0078d4 0%, #0078d4 100%)',
      text: '#ffffff',
      taskbar: '#ececec',
      taskbarBorder: '#d3d3d3',
      taskbarText: '#000000',
      iconBg: '#ffffff',
      iconText: '#000000',
      selectedIconBg: '#e0e0e0',
      selectionColor: '#0078d4',
      selectionBg: 'rgba(0, 120, 212, 0.2)',
    },
    linux: {
      name: 'Linux GNOME',
      desktop: '#2d2d2d',
      window: '#3d3d3d',
      border: { top: '#4d4d4d', left: '#4d4d4d', right: '#1d1d1d', bottom: '#1d1d1d' },
      title: 'linear-gradient(90deg, #1d1d1d 0%, #2d2d2d 100%)',
      text: '#ffffff',
      taskbar: '#2d2d2d',
      taskbarBorder: '#3d3d3d',
      taskbarText: '#ffffff',
      iconBg: '#2d2d2d',
      iconText: '#ffffff',
      selectedIconBg: '#4d4d4d',
      selectionColor: '#0099ff',
      selectionBg: 'rgba(0, 153, 255, 0.2)',
    },
  };

  const currentTheme = themes[osStyle];

  // Styled Lucide Icons with retro appearance
  const appIcons = {
    tripplanner: <StyledIcon Icon={Map} color="#fff" bgColor="#0088ff" />,
    npspage: <StyledIcon Icon={Mountain} color="#fff" bgColor="#228B22" />,
    gearpage: <StyledIcon Icon={Backpack} color="#fff" bgColor="#ff5555" />,
    vendors: <StyledIcon Icon={ShoppingBag} color="#fff" bgColor="#ffaa00" />,
    weather: <StyledIcon Icon={Cloud} color="#000" bgColor="#88ddff" />,
    headlines: <StyledIcon Icon={Newspaper} color="#000" bgColor="#f5f5f5" />,
    notepad: <StyledIcon Icon={FileText} color="#000" bgColor="#ffff99" />,
    filemanager: <FolderIcon size={32} color="#dfb939" />,
  };

  // Available applications
  const apps = [
    {
      id: "tripplanner",
      title: "Trip Planner",
      icon: appIcons.tripplanner,
      category: "Outdoor",
      component: <TripPlannerPage />,
      width: 700,
      height: 600,
    },
    {
      id: "npspage",
      title: "NPS Page",
      icon: appIcons.npspage,
      category: "Outdoor",
      component: <PlanPage />,
      width: 700,
      height: 600,
    },
    {
      id: "gearpage",
      title: "Gear Page",
      icon: appIcons.gearpage,
      category: "Outdoor",
      component: <GearPage />,
      width: 700,
      height: 600,
    },
    {
      id: "vendors",
      title: "Vendors",
      icon: appIcons.vendors,
      category: "Outdoor",
      component: <VendorsPage />,
      width: 700,
      height: 600,
    },
    {
      id: "providers",
      title: "Providers",
      icon: <StyledIcon Icon={ShoppingBag} color="#fff" bgColor="#E91E63" />,
      category: "Outdoor",
      component: <ProvidersPage />,
      width: 800,
      height: 600,
    },
    {
      id: "howtoguides",
      title: "How To Guides",
      icon: <StyledIcon Icon={BookOpen} color="#fff" bgColor="#FF9800" />,
      category: "Outdoor",
      component: <HowToPage />,
      width: 700,
      height: 600,
    },
    {
      id: "weather",
      title: "Weather",
      icon: appIcons.weather,
      category: "Outdoor",
      component: (
        <WeatherApp
          latitude={weatherLocation.latitude}
          longitude={weatherLocation.longitude}
          locationName={weatherLocation.locationName}
          onLocationChange={setWeatherLocation}
        />
      ),
      width: 600,
      height: 500,
    },
    {
      id: "headlines",
      title: "Headlines",
      icon: appIcons.headlines,
      category: "Tools",
      component: <HeadlinesPage />,
      width: 700,
      height: 600,
    },
    {
      id: "notepad",
      title: "Notepad",
      icon: appIcons.notepad,
      category: "Tools",
      component: <Notepad />,
      width: 600,
      height: 500,
    },
    {
      id: "filemanager",
      title: "File Manager",
      icon: appIcons.filemanager,
      category: "Tools",
      width: 700,
      height: 500,
    },
    {
      id: "systeminfo",
      title: "System Info",
      icon: <StyledIcon Icon={Newspaper} color="#000" bgColor="#f0f0f0" />,
      category: "System",
      width: 500,
      height: 400,
    },
    {
      id: "sync-admin",
      title: "Sync Admin",
      icon: <StyledIcon Icon={Mountain} color="#fff" bgColor="#FF9800" />,
      category: "System",
      component: <FacilitiesSyncAdmin />,
      width: 550,
      height: 450,
    },
    {
      id: "campgrounds",
      title: "Campgrounds",
      icon: <StyledIcon Icon={Tent} color="#fff" bgColor="#8B4513" />,
      category: "Outdoor",
      component: <CampgroundInfo />,
      width: 800,
      height: 600,
    },
    {
      id: "facilities",
      title: "Facilities",
      icon: <StyledIcon Icon={Building2} color="#fff" bgColor="#1976D2" />,
      category: "Outdoor",
      component: <FacilityFinder />,
      width: 800,
      height: 600,
    },
    {
      id: "solitaire",
      title: "Solitaire",
      icon: <StyledIcon Icon={Spade} color="#c00" bgColor="#f0f0f0" />,
      category: "Games",
      component: <Solitaire />,
      width: 600,
      height: 600,
    },
  ];

  // Launch a new window
  const launchApp = (appConfig) => {
    const newWindow = {
      id: Date.now(),
      appId: appConfig.id,
      title: appConfig.title,
      width: appConfig.width || 600,
      height: appConfig.height || 400,
      x: 50,
      y: 50,
      isMinimized: false,
      isMaximized: false,
      zIndex: zIndex,
    };
    setWindows((prevWindows) => [...prevWindows, newWindow]);
    setZIndex((prevZ) => prevZ + 1);
  };

  // Listen for app launch events from Start menu
  useEffect(() => {
    const handleLaunchApp = (event) => {
      const appId = event.detail.id;
      const app = apps.find((a) => a.id === appId);
      if (app) {
        launchApp({
          id: app.id,
          title: app.title,
          width: app.width,
          height: app.height,
        });
      }
    };

    window.addEventListener('launchApp', handleLaunchApp);
    return () => window.removeEventListener('launchApp', handleLaunchApp);
  }, [apps, zIndex]);

  // Render component for a window based on app ID
  const renderAppComponent = (appId) => {
    switch (appId) {
      case "tripplanner":
        return <TripPlannerPage />;
      case "npspage":
        return <PlanPage />;
      case "gearpage":
        return <GearPage />;
      case "vendors":
        return <VendorsPage />;
      case "providers":
        return <ProvidersPage />;
      case "howtoguides":
        return <HowToPage />;
      case "weather":
        return (
          <WeatherApp
            latitude={weatherLocation.latitude}
            longitude={weatherLocation.longitude}
            locationName={weatherLocation.locationName}
            onLocationChange={setWeatherLocation}
          />
        );
      case "headlines":
        return <HeadlinesPage />;
      case "notepad":
        return <Notepad />;
      case "filemanager":
        return (
          <FileExplorerWindow
            apps={apps}
            onLaunchApp={launchApp}
            theme={currentTheme}
            selectedIconId={fileExplorerState.selectedIconId}
            setSelectedIconId={(id) => {
              setFileExplorerState((prev) => ({
                ...prev,
                selectedIconId: id,
              }));
            }}
            currentPath={fileExplorerState.currentPath}
            setCurrentPath={(path) => {
              setFileExplorerState((prev) => ({
                ...prev,
                currentPath: path,
              }));
            }}
          />
        );
      case "systeminfo":
        return <SystemInfo />;
      case "sync-admin":
        return <FacilitiesSyncAdmin />;
      case "campgrounds":
        return <CampgroundInfo />;
      case "facilities":
        return <FacilityFinder />;
      case "solitaire":
        return <Solitaire />;
      default:
        return null;
    }
  };

  const closeWindow = (id) => {
    setWindows(windows.filter((w) => w.id !== id));
  };

  const minimizeWindow = (id) => {
    setWindows(
      windows.map((w) =>
        w.id === id ? { ...w, isMinimized: !w.isMinimized } : w
      )
    );
  };

  const maximizeWindow = (id) => {
    setWindows(
      windows.map((w) =>
        w.id === id
          ? {
              ...w,
              isMaximized: !w.isMaximized,
              width: !w.isMaximized ? "100%" : 600,
              height: !w.isMaximized ? "100%" : 400,
              x: !w.isMaximized ? 0 : 50,
              y: !w.isMaximized ? 0 : 50,
            }
          : w
      )
    );
  };

  const focusWindow = (id) => {
    const newZIndex = zIndex + 1;
    setZIndex(newZIndex);
    setWindows(
      windows.map((w) => (w.id === id ? { ...w, zIndex: newZIndex } : w))
    );
  };

  const restoreWindow = (id) => {
    // Explicitly restore (un-minimize) and focus
    const newZIndex = zIndex + 1;
    setZIndex(newZIndex);
    setWindows(
      windows.map((w) => 
        w.id === id ? { ...w, isMinimized: false, zIndex: newZIndex } : w
      )
    );
  };

  const updatePosition = (id, x, y) => {
    setWindows(
      windows.map((w) => (w.id === id ? { ...w, x, y } : w))
    );
  };

  const resizeWindow = (id, width, height) => {
    setWindows(
      windows.map((w) => (w.id === id ? { ...w, width, height } : w))
    );
  };

  return (
    <OSShell
      windows={windows}
      setWindows={setWindows}
      zIndex={zIndex}
      setZIndex={setZIndex}
      onCloseWindow={closeWindow}
      onMinimizeWindow={minimizeWindow}
      onMaximizeWindow={maximizeWindow}
      onFocusWindow={(id) => {
        focusWindow(id);
        setFocusedWindowId(id);
      }}
      onRestoreWindow={restoreWindow}
      onPositionChange={updatePosition}
      onResizeWindow={resizeWindow}
      theme={currentTheme}
      osStyle={osStyle}
      onOsStyleChange={setOsStyle}
      themes={themes}
      weatherLocation={weatherLocation}
      setWeatherLocation={setWeatherLocation}
      renderAppComponent={renderAppComponent}
      focusedWindowId={focusedWindowId}
    >
      <DesktopContent apps={apps} onLaunchApp={launchApp} theme={currentTheme} setFileExplorerState={setFileExplorerState} />
    </OSShell>
  );
};

// File Explorer Window Component
const FileExplorerWindow = ({
  apps,
  onLaunchApp,
  theme,
  selectedIconId,
  setSelectedIconId,
  currentPath,
  setCurrentPath,
}) => {
  const [contextMenu, setContextMenu] = useState(null);
  const explorerRef = useRef(null);

  // Group apps by category
  const groupedApps = apps.reduce((acc, app) => {
    if (!acc[app.category]) {
      acc[app.category] = [];
    }
    acc[app.category].push(app);
    return acc;
  }, {});

  const isRootDirectory = currentPath.length === 0;
  const currentCategory = currentPath[0];
  const currentApps = isRootDirectory ? [] : groupedApps[currentCategory] || [];

  const handleFolderClick = (categoryName) => {
    setCurrentPath([categoryName]);
  };

  const handleNavigateBack = () => {
    setCurrentPath([]);
  };

  const handleFileRightClick = (e, itemId) => {
    e.preventDefault();
    setSelectedIconId(itemId);
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        {
          label: "Open",
          onClick: () => {
            onLaunchApp({
              id: itemId,
              title: currentApps.find((a) => a.id === itemId)?.title,
              width: currentApps.find((a) => a.id === itemId)?.width,
              height: currentApps.find((a) => a.id === itemId)?.height,
            });
          },
        },
      ],
    });
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && selectedIconId && !isRootDirectory) {
        const app = currentApps.find((a) => a.id === selectedIconId);
        if (app) {
          onLaunchApp({
            id: app.id,
            title: app.title,
            width: app.width,
            height: app.height,
          });
        }
      } else if (e.key === "Escape") {
        setSelectedIconId(null);
      }
    };

    explorerRef.current?.addEventListener("keydown", handleKeyDown);
    return () => explorerRef.current?.removeEventListener("keydown", handleKeyDown);
  }, [selectedIconId, isRootDirectory, currentApps, onLaunchApp]);

  // Use passed theme or fallback to Windows 95 theme
  const explorerTheme = theme || {
    window: '#c0c0c0',
    text: '#000000',
    border: { top: '#dfdfdf', left: '#dfdfdf', right: '#808080', bottom: '#808080' },
    buttonFace: '#c0c0c0',
    buttonText: '#000000',
    buttonHighlight: '#dfdfdf',
    buttonShadow: '#808080',
  };

  return (
    <div
      ref={explorerRef}
      className="w-full h-full flex flex-col focus:outline-none"
      style={{ background: explorerTheme.window }}
      tabIndex={0}
    >
      {/* Toolbar */}
      <div
        className="flex items-center gap-2 p-2 border-b-2"
        style={{
          borderColor: explorerTheme.border.bottom,
          background: explorerTheme.window,
        }}
      >
        <button
          onClick={handleNavigateBack}
          disabled={isRootDirectory}
          className="px-2 py-1 text-xs font-bold"
          style={{
            background: explorerTheme.buttonFace,
            border: `2px solid`,
            borderColor: `${explorerTheme.border.top} ${explorerTheme.border.right} ${explorerTheme.border.bottom} ${explorerTheme.border.left}`,
            color: explorerTheme.buttonText,
            cursor: isRootDirectory ? "default" : "pointer",
            opacity: isRootDirectory ? 0.5 : 1,
          }}
        >
          ← Back
        </button>
        <div style={{ width: "1px", height: "20px", background: explorerTheme.border.right }} />
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <FolderIcon size={16} />
          <span>{isRootDirectory ? "My Computer" : currentCategory}</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Folder Tree */}
        <div
          className="w-48 border-r-2 p-2 overflow-auto"
          style={{
            borderColor: explorerTheme.border.right,
            background: explorerTheme.window,
          }}
        >
          <div className="text-xs font-bold mb-2" style={{ color: explorerTheme.text, display: "flex", alignItems: "center", gap: "4px" }}>
            <FolderIcon size={14} />
            <span>Folders</span>
          </div>
          {Object.keys(groupedApps).map((category) => (
            <div
              key={category}
              onClick={() => handleFolderClick(category)}
              className="px-2 py-1 mb-1 cursor-pointer text-xs border-l-2"
              style={{
                borderColor: currentCategory === category ? "#000080" : "transparent",
                background: currentCategory === category ? explorerTheme.buttonFace : "transparent",
                color: explorerTheme.text,
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <FolderIcon size={16} />
              <span>{category}</span>
            </div>
          ))}
        </div>

        {/* Main Content Area */}
        <div
          className="flex-1 p-4 overflow-auto"
          style={{ background: explorerTheme.window }}
          onClick={() => setSelectedIconId(null)}
        >
          {isRootDirectory ? (
            <div>
              <div className="text-xs font-bold mb-4" style={{ color: explorerTheme.text, display: "flex", alignItems: "center", gap: "4px" }}>
                <FolderIcon size={16} />
                <span>Folders ({Object.keys(groupedApps).length})</span>
              </div>
              <div className="grid grid-cols-4 gap-6">
                {Object.keys(groupedApps).map((category) => (
                  <div
                    key={category}
                    onDoubleClick={() => handleFolderClick(category)}
                    className="flex flex-col items-center cursor-pointer p-2 rounded"
                    style={{
                      backgroundColor: selectedIconId === category ? "#e0e0e0" : "transparent",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedIconId(category);
                    }}
                  >
                    <div className="mb-2">
                      <FolderIcon size={56} />
                    </div>
                    <div
                      className="text-xs text-center w-24 break-words font-semibold"
                      style={{ color: explorerTheme.text }}
                    >
                      {category}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <div className="text-xs font-bold mb-4" style={{ color: explorerTheme.text }}>
                📄 Files ({currentApps.length})
              </div>
              <div className="space-y-1">
                {currentApps.map((app) => (
                  <div
                    key={app.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedIconId(app.id);
                    }}
                    onDoubleClick={() => {
                      onLaunchApp({
                        id: app.id,
                        title: app.title,
                        width: app.width,
                        height: app.height,
                      });
                    }}
                    onContextMenu={(e) => handleFileRightClick(e, app.id)}
                    className="flex items-center gap-3 p-2 cursor-pointer rounded"
                    style={{
                      backgroundColor: selectedIconId === app.id ? "#e0e0e0" : "transparent",
                    }}
                  >
                    <div className="w-8 h-8 flex items-center justify-center">{app.icon}</div>
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold" style={{ color: explorerTheme.text }}>
                        {app.title}
                      </span>
                      <span className="text-xs" style={{ color: explorerTheme.text, opacity: 0.6 }}>
                        Application
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status Bar */}
      <div
        className="border-t-2 p-2 text-xs flex justify-between"
        style={{
          borderColor: explorerTheme.border.top,
          background: explorerTheme.buttonFace,
          color: explorerTheme.buttonText,
        }}
      >
        <span>
          {isRootDirectory
            ? `${Object.keys(groupedApps).length} folders`
            : `${currentApps.length} items`}
        </span>
        <span>{currentCategory || "My Computer"}</span>
      </div>

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenu.items}
          theme={explorerTheme}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
};

// Desktop Folder Component
const DesktopFolder = ({ name, theme, isSelected, onSelect, onDoubleClick }) => {
  return (
    <div
      className="flex flex-col items-center cursor-pointer select-none p-2 rounded"
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onDoubleClick();
      }}
      style={{
        backgroundColor: isSelected ? "#e0e0e0" : "transparent",
      }}
    >
      <div className="mb-2">
        <FolderIcon size={56} />
      </div>
      <div
        className="text-xs text-center w-16 break-words"
        style={{ color: theme.iconText }}
      >
        {name}
      </div>
    </div>
  );
};

// Desktop Content Component
const DesktopContent = ({ apps, onLaunchApp, theme, setFileExplorerState }) => {
  const [selectedIconId, setSelectedIconId] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const desktopRef = useRef(null);

  // Group apps by category
  const groupedApps = apps.reduce((acc, app) => {
    if (!acc[app.category]) {
      acc[app.category] = [];
    }
    acc[app.category].push(app);
    return acc;
  }, {});

  const categories = Object.keys(groupedApps);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedIconId) return;

      if (e.key === "Enter") {
        handleFolderDoubleClick(selectedIconId);
      } else if (e.key === "Escape") {
        setSelectedIconId(null);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        const currentIdx = categories.indexOf(selectedIconId);
        if (currentIdx < categories.length - 1) {
          setSelectedIconId(categories[currentIdx + 1]);
        }
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        const currentIdx = categories.indexOf(selectedIconId);
        if (currentIdx > 0) {
          setSelectedIconId(categories[currentIdx - 1]);
        }
      }
    };

    desktopRef.current?.addEventListener("keydown", handleKeyDown);
    return () => desktopRef.current?.removeEventListener("keydown", handleKeyDown);
  }, [selectedIconId, categories]);

  const handleDesktopRightClick = (e) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        {
          label: "Refresh",
          onClick: () => {
            setSelectedIconId(null);
          },
        },
      ],
    });
  };

  const handleFolderDoubleClick = (categoryName) => {
    // Set the initial path to the selected folder before launching the File Manager
    setFileExplorerState((prev) => ({
      ...prev,
      currentPath: [categoryName],
    }));
    onLaunchApp({
      id: "filemanager",
      title: "File Manager",
      width: 700,
      height: 500,
    });
  };

  return (
    <div
      ref={desktopRef}
      className="w-full h-full p-4 relative overflow-auto focus:outline-none"
      style={{ background: theme.desktop }}
      onClick={() => setSelectedIconId(null)}
      onContextMenu={handleDesktopRightClick}
      tabIndex={0}
    >
      {/* Desktop Folders */}
      <div className="grid grid-cols-4 gap-8 w-max pt-4">
        {Object.keys(groupedApps).map((category) => (
          <DesktopFolder
            key={category}
            name={category}
            theme={theme}
            isSelected={selectedIconId === category}
            onSelect={() => setSelectedIconId(category)}
            onDoubleClick={() => handleFolderDoubleClick(category)}
          />
        ))}
      </div>

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenu.items}
          theme={theme}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
};

// Desktop Icon Component
const DesktopIcon = ({ icon, label, onDoubleClick, theme, isSelected, onSelect }) => {
  return (
    <div
      className="flex flex-col items-center gap-2 p-2 w-20 cursor-pointer select-none"
      style={{
        background: isSelected ? theme.selectedIconBg : "transparent",
      }}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onDoubleClick();
      }}
    >
      <div className="text-4xl">{icon}</div>
      <span
        className="text-xs text-center font-bold"
        style={{ color: theme.iconText }}
      >
        {label}
      </span>
    </div>
  );
};

export default OSShellDemoPage;